import { useEffect, useRef, type RefObject } from "react";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function useStableCallback<T extends (...args: any[]) => any>(fn: T | undefined): T | undefined {
  const ref = useRef(fn);
  ref.current = fn;
  return useRef((...args: Parameters<T>) => ref.current?.(...args) as ReturnType<T>).current as
    | T
    | undefined;
}

/**
 * Trap keyboard focus inside a dialog while ``open`` is true (WCAG 2.1.2,
 * inverse of "no keyboard trap" - the user must be able to Tab forward
 * and back without escaping the modal).
 *
 * Behaviour:
 *   * On ``open`` → moves focus to the first focusable element inside
 *     the container, after a tick so DOM children have mounted.
 *   * Tab / Shift+Tab cycle through focusables (computed live each
 *     keypress so dynamic content stays in the cycle).
 *   * Esc calls ``onClose`` (each modal already wires its own Esc;
 *     centralising here lets every modal opt-out by passing
 *     ``escClosesDialog: false`` if they want different semantics).
 *   * On unmount or ``open`` flipping false → restores focus to the
 *     element that was focused before the modal opened.
 *
 * Returns a ref that the modal must attach to its outermost element.
 * The element doesn't need to be focusable itself; the hook walks its
 * descendants.
 */
export function useFocusTrap<T extends HTMLElement = HTMLDivElement>(
  open: boolean,
  onClose?: () => void,
  options: { escClosesDialog?: boolean } = {},
): RefObject<T | null> {
  const containerRef = useRef<T | null>(null);
  const escClosesDialog = options.escClosesDialog ?? true;
  const stableOnClose = useStableCallback(onClose);

  useEffect(() => {
    if (!open) return;

    const previouslyFocused = document.activeElement as HTMLElement | null;

    const onKey = (e: KeyboardEvent): void => {
      if (escClosesDialog && e.key === "Escape" && stableOnClose) {
        stableOnClose();
        return;
      }
      if (e.key !== "Tab" || !containerRef.current) return;
      const focusables = Array.from(
        containerRef.current.querySelectorAll<HTMLElement>(
          'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ),
      ).filter((el) => !el.hasAttribute("aria-hidden") && el.offsetParent !== null);
      if (focusables.length === 0) return;
      const first = focusables[0]!;
      const last = focusables[focusables.length - 1]!;
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    // Defer initial focus by a microtask so the modal's children have
    // had a chance to render. Without this, querySelector sees an
    // empty container and we can't move focus inside.
    const focusTimer = window.setTimeout(() => {
      const focusable = containerRef.current?.querySelector<HTMLElement>(
        'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      focusable?.focus();
    }, 0);

    document.addEventListener("keydown", onKey);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKey);
      // Restore focus only if the previous element is still in the DOM
      // and focusable. Without the connectedness check, restoring a
      // detached element silently fails and focus goes to <body>.
      if (previouslyFocused && document.body.contains(previouslyFocused)) {
        previouslyFocused.focus();
      }
    };
  }, [open, stableOnClose, escClosesDialog]);

  return containerRef;
}
